import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useMatch } from "react-router-dom";

import * as analytics from "@utils/analytics";

/*
 * Language state for Sophia's storefront only.
 *
 * Chinese is the default; English is the alternative. The rest of the community
 * app is English-only: outside the storefront route, useStorefrontLocale() always
 * reports "en", whatever the visitor picked on the storefront.
 *
 * The choice is remembered in localStorage. A ?lang=zh / ?lang=en link overrides
 * it (handy for posting an English link to an English-speaking audience).
 */

const DEFAULT_LANG = "zh";
const LANGS = ["zh", "en"];
const STORAGE_KEY = "winvest.storefront.lang";
const STOREFRONT_EXPERIMENT = "book_waitlist_zh_default_v1";

const STOREFRONT_PATH = "/sophia-lan-storefront";
const STOREFRONT_PATH_PORTFOLIO = "/creator-storefront";

const readUrlLang = () => {
  try {
    const value = new URLSearchParams(window.location.search).get("lang");
    return LANGS.includes(value) ? value : null;
  } catch (_) {
    return null;
  };
};

const readSavedLang = () => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return LANGS.includes(value) ? value : null;
  } catch (_) {
    return null;
  };
};

const saveLang = (value) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch (_) {};
};

// Where the starting language came from, sent with the page view so we can
// tell a default-Chinese visit apart from a returning visitor's own choice.
const initialState = () => {
  const fromUrl = readUrlLang();
  if (fromUrl) {
    saveLang(fromUrl);
    return { lang: fromUrl, source: "url" };
  };
  const saved = readSavedLang();
  if (saved) return { lang: saved, source: "saved" };
  return { lang: DEFAULT_LANG, source: "default" };
};

const StorefrontLocaleContext = createContext({
  lang: DEFAULT_LANG,
  source: "default",
  setLang: () => {},
});

const StorefrontLocaleProvider = ({ children }) => {
  const [state, setState] = useState(initialState);

  const setLang = useCallback((next) => {
    if (!LANGS.includes(next)) return;
    saveLang(next);
    setState({ lang: next, source: "picked" });
  }, []);

  const value = useMemo(() => ({ ...state, setLang }), [state, setLang]);

  return (
    <StorefrontLocaleContext.Provider value={value}>
      {children}
    </StorefrontLocaleContext.Provider>
  );
};

/**
 * { onStorefront, lang, source, setLang }
 * `lang` is the visitor's choice on the storefront route and always "en" elsewhere.
 */
const useStorefrontLocale = () => {
  const onStorefront = Boolean(useMatch(STOREFRONT_PATH))
  const onPortfolioStorefront = Boolean(useMatch(STOREFRONT_PATH_PORTFOLIO));

  const { lang, source, setLang } = useContext(StorefrontLocaleContext);
  return {
    onStorefront,
    onPortfolioStorefront,
    lang: onStorefront ? lang : "en",
    source,
    setLang,
  };
};

/* ------------------------------------------------------------------ */
/* Tracking                                                            */
/* ------------------------------------------------------------------ */

const INTERNAL_KEY = "winvest.storefront.internal";

/*
 * Where this visit came from, read once from the landing URL. Use tagged links
 * in DMs, e.g. /sophia-lan-storefront?utm_source=rednote&utm_medium=dm&utm_campaign=allocation_video
 * Visits without tags are reported as "direct".
 *
 * Team visits: open the page once with ?internal=1 on each of your devices and
 * every event from that browser is tagged internal: true, so you can filter it
 * out of a small sample. ?internal=0 clears it.
 */
const readAttribution = () => {
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (_) {
    params = new URLSearchParams();
  }

  let internal = false;
  try {
    const flag = params.get("internal");
    if (flag === "1") window.localStorage.setItem(INTERNAL_KEY, "1");
    if (flag === "0") window.localStorage.removeItem(INTERNAL_KEY);
    internal = window.localStorage.getItem(INTERNAL_KEY) === "1";
  } catch (_) {
    internal = params.get("internal") === "1";
  }

  return {
    source: params.get("utm_source") || params.get("ref") || "direct",
    medium: params.get("utm_medium") || undefined,
    campaign: params.get("utm_campaign") || undefined,
    internal,
  };
};

let attribution = null;
const getAttribution = () => {
  if (!attribution) attribution = readAttribution();
  return attribution;
};

/*
 * Amplitude is loaded asynchronously from its CDN (src/index.js, production
 * only), so window.amplitude often doesn't exist yet when the page first
 * renders, and the page view would be silently dropped. Events wait in a
 * queue until Amplitude is ready, checked every 250 ms for up to 20 s.
 */
const pending = [];
let waitTimer = null;
let waitedMs = 0;
const POLL_MS = 250;
const MAX_WAIT_MS = 20000;

const amplitudeReady = () => (
  typeof window !== "undefined"
  && window.amplitude
  && typeof window.amplitude.track === "function"
);

const flush = () => {
  while (pending.length && amplitudeReady()) {
    const [name, props] = pending.shift();
    analytics.track(name, props);
  }
};

const waitForAmplitude = () => {
  if (waitTimer) return;
  waitTimer = setInterval(() => {
    waitedMs += POLL_MS;
    if (amplitudeReady() || waitedMs >= MAX_WAIT_MS) {
      clearInterval(waitTimer);
      waitTimer = null;
      flush();
      // Still not loaded (dev build, ad blocker): drop rather than grow forever.
      pending.length = 0;
    }
  }, POLL_MS);
};

/**
 * Every storefront event carries the experiment id, the traffic source and
 * the internal flag, so the funnel can be filtered by any of them.
 */
export function trackStorefront(name, props) {
  const event = [
    name,
    { experiment: STOREFRONT_EXPERIMENT, ...getAttribution(), ...(props || {}) },
  ];
  if (amplitudeReady()) {
    flush();
    analytics.track(...event);
  } else {
    pending.push(event);
    waitForAmplitude();
  }
};

export {
  STOREFRONT_EXPERIMENT,
  STOREFRONT_PATH,
  StorefrontLocaleProvider,
  useStorefrontLocale,
};
